import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Card, Btn, Empty } from "../../components/UI";
import { ShieldAlert, CheckCircle2, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SEV = ["", "contraindicated", "major", "moderate", "minor"];
const SEV_TONE = {
  contraindicated: "bg-[#C62828] text-white",
  major: "bg-[#B26A00] text-white",
  moderate: "bg-[#FFB300] text-[#3E2723]",
  minor: "bg-[#EFEBE0] text-[#6D4C41]",
};
const fmt = (d) => (d ? dayjs(d).format("MMM D, YYYY h:mm A") : "-");

export default function DdiAudit() {
  const { user } = useAuth();
  const canView = user && (user.role === "admin" || user.role === "clinician");
  const canReview = canView;
  const [rows, setRows] = useState([]);
  const [severity, setSeverity] = useState("");
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (severity) params.severity = severity;
    if (unreviewedOnly) params.unreviewed_only = true;
    api.get("/medical/ddi-overrides", { params }).then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [severity, unreviewedOnly]); // eslint-disable-line

  const filtered = useMemo(() => rows.filter((o) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [o.medication, o.client_name, o.clinician_name, o.reason,
            ...(o.interactions || []).map((h) => `${h.other} ${h.effect}`)].some((x) => (x || "").toLowerCase().includes(s));
  }), [rows, q]);

  const stats = useMemo(() => ({
    total: rows.length,
    unreviewed: rows.filter((r) => !r.reviewed_at).length,
    contraindicated: rows.filter((r) => r.top_severity === "contraindicated").length,
    major: rows.filter((r) => r.top_severity === "major").length,
  }), [rows]);

  const markReviewed = async (id) => {
    try { await api.post(`/medical/ddi-overrides/${id}/review`); toast.success("Marked reviewed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (user && !canView) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="space-y-4" data-testid="ddi-audit-page">
      <PageHeader title="DDI Override Audit"
        subtitle="Every interaction acknowledgement clinicians log when overriding a drug-drug interaction warning." />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Total overrides</div>
          <div className="font-heading text-3xl font-bold text-[#3E2723]" data-testid="ddi-audit-total">{stats.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Unreviewed</div>
          <div className="font-heading text-3xl font-bold text-[#8D5A00]" data-testid="ddi-audit-unreviewed">{stats.unreviewed}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Contraindicated</div>
          <div className="font-heading text-3xl font-bold text-[#C62828]" data-testid="ddi-audit-contra">{stats.contraindicated}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] font-bold uppercase text-[#7B6157]">Major</div>
          <div className="font-heading text-3xl font-bold text-[#8D5A00]" data-testid="ddi-audit-major">{stats.major}</div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
            <input data-testid="ddi-audit-search" placeholder="Search medication, client, clinician, or rationale…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="rounded-full border border-[#E5DFD3] bg-white pl-8 pr-4 py-1.5 text-xs outline-none w-80 focus:border-[#8D6E63]" />
          </div>
          <select data-testid="ddi-audit-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}
            className="rounded-full border border-[#E5DFD3] bg-white px-3 py-1.5 text-xs outline-none focus:border-[#8D6E63]">
            {SEV.map((s) => <option key={s || "all"} value={s}>{s ? s : "All severities"}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs font-bold text-[#5D4037]">
            <input data-testid="ddi-audit-unreviewed-toggle" type="checkbox" checked={unreviewedOnly}
              onChange={(e) => setUnreviewedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-[#E5DFD3] text-[#4E342E]" />
            Unreviewed only
          </label>
          <div className="ml-auto text-xs text-[#7B6157]">{filtered.length} record{filtered.length === 1 ? "" : "s"}</div>
        </div>
      </Card>

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-[#EFEBE9]" />
      ) : filtered.length === 0 ? (
        <Card className="p-4"><Empty text="No DDI override records match the current filters." /></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`ddi-audit-row-${o.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEV_TONE[o.top_severity] || "bg-[#EFEBE0] text-[#6D4C41]"}`}>
                  {o.top_severity || "unknown"}
                </span>
                <span className="text-base font-bold text-[#3E2723]">{o.medication}</span>
                <span className="text-[11px] text-[#7B6157]">for</span>
                <Link to={`/clients/${o.client_id}/console`} className="text-sm font-bold text-[#4E342E] hover:underline flex items-center gap-1"
                  data-testid={`ddi-audit-client-${o.id}`}>
                  {o.client_name} <ExternalLink size={11} />
                </Link>
                <span className="ml-auto text-[11px] text-[#7B6157]">
                  {o.clinician_name} · {fmt(o.created_at)}
                </span>
              </div>

              <div className="mt-2 space-y-1">
                {(o.interactions || []).map((h, i) => (
                  <div key={i} className="rounded-lg border border-[#EFEBE0] bg-[#FDFCF8] p-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SEV_TONE[h.severity] || "bg-[#EFEBE0] text-[#6D4C41]"}`}>{h.severity}</span>
                      <span className="font-semibold text-[#3E2723]">with {h.other}</span>
                    </div>
                    <div className="mt-1 text-[#5D4037]"><strong>Effect:</strong> {h.effect}</div>
                    <div className="text-[#5D4037]"><strong>Management:</strong> {h.management}</div>
                  </div>
                ))}
              </div>

              <div className="mt-2 rounded-lg bg-[#F5F2EB] p-2 text-xs text-[#4E342E]">
                <span className="font-bold">Clinical rationale:</span> {o.reason || "-"}
              </div>

              <div className="mt-3 flex items-center justify-between">
                {o.reviewed_at ? (
                  <div className="flex items-center gap-1 text-xs font-bold text-[#2E7D32]">
                    <CheckCircle2 size={13} /> Reviewed by {o.reviewed_by_name} · {fmt(o.reviewed_at)}
                  </div>
                ) : (
                  <div className="text-xs font-bold text-[#8D5A00]">Awaiting supervisor review</div>
                )}
                {!o.reviewed_at && canReview && (
                  <Btn data-testid={`ddi-audit-review-${o.id}`} onClick={() => markReviewed(o.id)}>
                    Mark reviewed
                  </Btn>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
